// File: CarWizardConfiguratorNew - dynamic (steps/screens/rules) wizard
// Created: 2026-05-12
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchCar, createConfiguration } from "../services/api.js";
import "./VehicleConfigurator.css";

import LayeredPreview from "./carWizard/LayeredPreview";
import StepOptions from "./carWizard/StepOptions";
import ContactForm, { type ContactData } from "./carWizard/ContactForm";
import SummaryPanel from "./carWizard/SummaryPanel";
import ConfiguratorFooter from "./carWizard/ConfiguratorFooter";
import type { CarConfig, Option, Overlay, Scenery, Screen, Step, SubOption, ViewKey } from "./carWizard/carWizardTypes";
import { VIEW_KEYS } from "./carWizard/carWizardTypes";
import {
  findStepIdOfOption, isRenderableImage, normalizeCar, overlayLayerOrder, SCENERY_LAYER_ORDER, SCENERY_SHADOW_LAYER_ORDER,
} from "./carWizard/carModel";
import { evaluate, fixedSubOptions, optionKey, resolveActiveSubOption } from "./carWizard/rules";
import { IS_PREVIEW } from "../config.js";

interface CarWizardConfiguratorNewProps {
  containerId?: string;
  carData?: CarConfig | null;
  embedded?: boolean;
  // Hydration from a saved share (/c/:slug).
  initialSelections?: Record<string, string>;
  initialSubSelections?: Record<string, string>;
  initialOverlayIds?: string[];
  initialSceneryId?: string | null;
  startInSummary?: boolean;
  // Shown as a "Change model" link when the configurator was opened from the splash.
  onChangeModel?: () => void;
}

type VariantMode = "matching" | "fixed";
type Selections = Record<string, string>; // stepId -> optionId

const defaultOverlayIds = (c: CarConfig) => (c.overlays || []).filter((o) => o.defaultOn).map((o) => o.overlayId);
const defaultSceneryId = (c: CarConfig) => (c.sceneries || []).find((s) => s.isDefault)?.sceneryId ?? null;

const CarWizardConfiguratorNew = ({ containerId = "root", carData, embedded = false, initialSelections, initialSubSelections, initialOverlayIds, initialSceneryId, startInSummary, onChangeModel }: CarWizardConfiguratorNewProps) => {
  const [car, setCar] = useState<CarConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const optionsScrollRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);
  const [selectedView, setSelectedView] = useState<ViewKey>("view1");
  const [selectedOptions, setSelectedOptions] = useState<Selections>({});
  const [selectedSubOptions, setSelectedSubOptions] = useState<Selections>({});
  const [variantModes, setVariantModes] = useState<Record<string, VariantMode>>({});
  const [activeOverlayIds, setActiveOverlayIds] = useState<string[]>([]);
  const [sceneryId, setSceneryId] = useState<string | null>(null); // null = white studio
  const [showSummary, setShowSummary] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<{ [key: string]: boolean }>({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [optionsFading, setOptionsFading] = useState(false);
  const [formData, setFormData] = useState<ContactData>({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [formErrors, setFormErrors] = useState({ firstName: '', lastName: '', email: '', phone: '' });

  // Reset scroll to top on screen / panel change.
  useEffect(() => {
    const doScroll = () => {
      const el = optionsScrollRef.current;
      if (el) {
        el.scrollTop = 0;
        el.scrollTo({ top: 0, behavior: 'auto' });
      }
      try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch {}
    };
    const r1 = requestAnimationFrame(() => {
      doScroll();
      requestAnimationFrame(doScroll);
    });
    const t = setTimeout(doScroll, 250);
    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t);
    };
  }, [currentScreenIndex, showSummary, showContact]);

  const getCarIdentifiersFromContainer = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const carIdFromUrl = params.get('carId');
      const createdAtFromUrl = params.get('createdAt');
      if (carIdFromUrl && createdAtFromUrl) {
        return { carId: carIdFromUrl, createdAt: createdAtFromUrl };
      }
    } catch {
      // ignore
    }

    const container = document.getElementById(containerId);
    const carRecord = container?.getAttribute("data-car-record");
    if (!carRecord) return { carId: null, createdAt: null };
    const [carId, createdAt] = carRecord.split("|");
    return { carId, createdAt };
  };

  /* ------------------------------------------------------------------ *
   * Defaults: data-driven initial selection.
   * ------------------------------------------------------------------ */
  const buildDefaults = (c: CarConfig) => {
    // Steps that another option's sub-options are coupled to must be selected so
    // couplings can resolve (e.g. interior color).
    const linkedTargetStepIds = new Set<string>();
    c.steps.forEach((s) => s.options.forEach((o) => {
      if (o.subOptionsLinkedToStepId) linkedTargetStepIds.add(o.subOptionsLinkedToStepId);
    }));

    const baseStepId = c.baseRenderOptionId ? findStepIdOfOption(c, c.baseRenderOptionId) : null;

    const options: Selections = {};
    c.steps.forEach((step) => {
      if (!step.options.length) return;
      let chosen: Option | undefined;
      if (baseStepId === step.stepId && c.baseRenderOptionId) {
        chosen = step.options.find((o) => o.optionId === c.baseRenderOptionId);
      }
      chosen ??= step.options.find((o) => o.isDefault);
      chosen ??= step.options.find((o) => o.includedInBaseRender);
      if (!chosen && (step.required || linkedTargetStepIds.has(step.stepId))) {
        chosen = step.options[0];
      }
      if (chosen) options[step.stepId] = chosen.optionId;
    });

    // Second pass: resolve sub-options now that main selections are known.
    const subOptions: Selections = {};
    const modes: Record<string, VariantMode> = {};
    c.steps.forEach((step) => {
      const opt = step.options.find((o) => o.optionId === options[step.stepId]);
      if (!opt?.subOptions?.length) return;
      if (opt.subOptionsLinkedToStepId) {
        modes[step.stepId] = "matching";
        const active = resolveActiveSubOption(opt, options) || fixedSubOptions(opt)[0] || opt.subOptions[0];
        if (active) subOptions[step.stepId] = active.optionId;
      } else {
        const def = opt.subOptions.find((s) => s.isDefault) || opt.subOptions[0];
        if (def) subOptions[step.stepId] = def.optionId;
      }
    });

    return { options, subOptions, modes };
  };

  useEffect(() => {
    const loadCar = async () => {
      try {
        // Live-preview updates (admin re-passes carData on every edit): refresh
        // the car in place WITHOUT a loading flash and WITHOUT resetting the
        // user's selections, so layer reorders/edits show in real time. Only
        // missing selections get defaults (e.g. a newly added step).
        if (initializedRef.current && carData) {
          const normalized = normalizeCar(carData);
          setCar(normalized);
          const { options, subOptions, modes } = buildDefaults(normalized);
          setSelectedOptions((prev) => {
            const next = { ...prev };
            for (const s of normalized.steps) if (next[s.stepId] === undefined && options[s.stepId] !== undefined) next[s.stepId] = options[s.stepId];
            for (const k of Object.keys(next)) if (!normalized.steps.some((s) => s.stepId === k)) delete next[k];
            return next;
          });
          setSelectedSubOptions((prev) => {
            const next = { ...prev };
            for (const s of normalized.steps) if (next[s.stepId] === undefined && subOptions[s.stepId] !== undefined) next[s.stepId] = subOptions[s.stepId];
            return next;
          });
          setVariantModes((prev) => ({ ...modes, ...prev }));
          const overlayIds = new Set((normalized.overlays || []).map((o) => o.overlayId));
          setActiveOverlayIds((prev) => prev.filter((id) => overlayIds.has(id)));
          setSceneryId((prev) => (prev && (normalized.sceneries || []).some((s) => s.sceneryId === prev) ? prev : defaultSceneryId(normalized)));
          return;
        }

        setLoading(true);
        setError(null);

        let fetched: CarConfig;
        if (carData) {
          fetched = carData;
        } else {
          const { carId, createdAt } = getCarIdentifiersFromContainer();
          if (!carId || !createdAt) {
            throw new Error('Missing or invalid data-car-record on container element. Expected format: "id|createdAt"');
          }
          fetched = await fetchCar(carId, createdAt);
        }

        const normalized = normalizeCar(fetched);
        setCar(normalized);

        const { options, subOptions, modes } = buildDefaults(normalized);
        let nextOptions = { ...options };
        let nextSubOptions = { ...subOptions };
        let nextModes = { ...modes };
        let nextOverlayIds = initialOverlayIds ?? defaultOverlayIds(normalized);
        let nextSceneryId = initialSceneryId !== undefined && initialSceneryId !== null ? initialSceneryId : defaultSceneryId(normalized);

        // Hydration from a saved share (/c/:slug) passed as props.
        if (initialSelections) nextOptions = { ...nextOptions, ...initialSelections };
        if (initialSubSelections) nextSubOptions = { ...nextSubOptions, ...initialSubSelections };
        if (startInSummary) setShowSummary(true);

        // Shared configuration via URL (or sessionStorage fallback).
        const urlParams = new URLSearchParams(window.location.search);
        const configParamFromUrl = urlParams.get('config');
        const configParam = (() => {
          const storageKey = 'sharedConfigParam';
          if (configParamFromUrl) {
            try { sessionStorage.setItem(storageKey, configParamFromUrl); } catch {}
            return configParamFromUrl;
          }
          try { return sessionStorage.getItem(storageKey); } catch { return null; }
        })();

        if (configParam) {
          try {
            const tryParse = (raw: string) => {
              try { return JSON.parse(decodeURIComponent(raw)); }
              catch { return JSON.parse(decodeURIComponent(decodeURIComponent(raw))); }
            };
            const shared = tryParse(configParam);
            if (shared?.options) nextOptions = { ...nextOptions, ...shared.options };
            if (shared?.subOptions) nextSubOptions = { ...nextSubOptions, ...shared.subOptions };
            if (shared?.variantModes) nextModes = { ...nextModes, ...shared.variantModes };
            if (shared?.view) setSelectedView(shared.view);
            if (Array.isArray(shared?.overlays)) nextOverlayIds = shared.overlays;
            if (typeof shared?.scenery === 'string') nextSceneryId = shared.scenery;

            setShowSummary(true);
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          } catch (e) {
            console.error('Error parsing shared config:', e);
          }
        }

        setSelectedOptions(nextOptions);
        setSelectedSubOptions(nextSubOptions);
        setVariantModes(nextModes);
        const knownOverlays = new Set((normalized.overlays || []).map((o) => o.overlayId));
        setActiveOverlayIds(nextOverlayIds.filter((id) => knownOverlays.has(id)));
        setSceneryId(nextSceneryId && (normalized.sceneries || []).some((s) => s.sceneryId === nextSceneryId) ? nextSceneryId : null);
        initializedRef.current = true;
        setLoading(false);
      } catch (err: any) {
        console.error("Configuration load error:", err);
        setError(err.message || "Failed to load car configuration");
        setLoading(false);
      }
    };

    loadCar();
  }, [containerId, carData]);

  /* ------------------------------------------------------------------ *
   * Rule evaluation (recomputed on every selection change).
   * ------------------------------------------------------------------ */
  const effects = useMemo(() => evaluate(car?.rules || [], selectedOptions), [car, selectedOptions]);

  const getStep = (stepId: string): Step | undefined => car?.steps.find((s) => s.stepId === stepId);

  /** Variant locked by a forceSubOption rule for the step's current option, if any. */
  const getForcedSubOptionId = (stepId: string): string | null => {
    const forced = effects.forcedSubOptions.get(stepId);
    return forced && forced.optionId === selectedOptions[stepId] ? forced.subOptionId : null;
  };

  const getSelectedOption = (stepId: string): Option | null => {
    const step = getStep(stepId);
    return step?.options.find((o) => o.optionId === selectedOptions[stepId]) ?? null;
  };

  /** The image-bearing sub-option currently active for a step (coupled or not). */
  const getActiveSubOption = (stepId: string): SubOption | null => {
    const opt = getSelectedOption(stepId);
    if (!opt?.subOptions?.length) return null;
    // A forceSubOption rule wins over matching / manual variants.
    const forced = getForcedSubOptionId(stepId);
    if (forced) {
      const sub = opt.subOptions.find((s) => s.optionId === forced);
      if (sub) return sub;
    }
    if (opt.subOptionsLinkedToStepId) {
      const mode = variantModes[stepId] || "matching";
      if (mode === "fixed") {
        const chosen = opt.subOptions.find((s) => s.optionId === selectedSubOptions[stepId] && !s.linkedOptionId);
        return chosen || fixedSubOptions(opt)[0] || null;
      }
      return resolveActiveSubOption(opt, selectedOptions) || fixedSubOptions(opt)[0] || opt.subOptions[0] || null;
    }
    return opt.subOptions.find((s) => s.optionId === selectedSubOptions[stepId]) ?? null;
  };

  /* ------------------------------------------------------------------ *
   * Selection handlers.
   * ------------------------------------------------------------------ */
  const handleOptionChange = (stepId: string, optionId: string, applyPreferredView = true) => {
    setSelectedOptions((prev) => ({ ...prev, [stepId]: optionId }));

    const step = getStep(stepId);
    const option = step?.options.find((o) => o.optionId === optionId);

    if (applyPreferredView && car && !showSummary && !showContact) {
      const preferred = step?.preferredView;
      if (preferred && preferred !== 'no_change' && preferred !== selectedView) {
        setSelectedView(preferred);
      }
    }

    // Resolve sub-option for the new option.
    if (option?.subOptions?.length) {
      if (option.subOptionsLinkedToStepId) {
        const mode = variantModes[stepId] || "matching";
        const active = mode === "fixed"
          ? fixedSubOptions(option)[0]
          : resolveActiveSubOption(option, { ...selectedOptions, [stepId]: optionId }) || fixedSubOptions(option)[0] || option.subOptions[0];
        setVariantModes((p) => ({ ...p, [stepId]: mode }));
        if (active) setSelectedSubOptions((p) => ({ ...p, [stepId]: active.optionId }));
      } else {
        const def = option.subOptions.find((s) => s.isDefault) || option.subOptions[0];
        if (def) setSelectedSubOptions((p) => ({ ...p, [stepId]: def.optionId }));
      }
    } else {
      setSelectedSubOptions((prev) => {
        if (!prev[stepId]) return prev;
        const next = { ...prev };
        delete next[stepId];
        return next;
      });
    }
  };

  const handleSubOptionChange = (stepId: string, subOptionId: string) => {
    setSelectedSubOptions((prev) => ({ ...prev, [stepId]: subOptionId }));
  };

  // Coupled-option variant selector (e.g. steering "Match interior color" vs fixed).
  const handleVariantMode = (stepId: string, mode: VariantMode, fixedSubId?: string) => {
    setVariantModes((p) => ({ ...p, [stepId]: mode }));
    const opt = getSelectedOption(stepId);
    if (!opt) return;
    if (mode === "fixed") {
      const sub = (fixedSubId && opt.subOptions?.find((s) => s.optionId === fixedSubId)) || fixedSubOptions(opt)[0];
      if (sub) setSelectedSubOptions((p) => ({ ...p, [stepId]: sub.optionId }));
    } else {
      const active = resolveActiveSubOption(opt, selectedOptions) || opt.subOptions?.[0];
      if (active) setSelectedSubOptions((p) => ({ ...p, [stepId]: active.optionId }));
    }
  };

  // Apply forced options from rules (they also disable their step in the UI).
  useEffect(() => {
    if (!car) return;
    effects.forcedOptions.forEach((optionId, stepId) => {
      if (selectedOptions[stepId] !== optionId) {
        handleOptionChange(stepId, optionId, false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effects, car]);

  /* ------------------------------------------------------------------ *
   * Overlays (frame, soft top, ...) and sceneries.
   * ------------------------------------------------------------------ */
  const getActiveOverlays = (): Overlay[] =>
    (car?.overlays || []).filter((ov) => activeOverlayIds.includes(ov.overlayId));

  const getActiveScenery = (): Scenery | null =>
    (sceneryId && car?.sceneries?.find((s) => s.sceneryId === sceneryId)) || null;

  const toggleOverlay = (overlayId: string) => {
    const overlay = car?.overlays?.find((o) => o.overlayId === overlayId);
    if (!overlay) return;
    setActiveOverlayIds((prev) => {
      if (prev.includes(overlayId)) return prev.filter((id) => id !== overlayId);
      const group = overlay.exclusiveGroup?.trim();
      const kept = group
        ? prev.filter((id) => car?.overlays?.find((o) => o.overlayId === id)?.exclusiveGroup?.trim() !== group)
        : prev;
      return [...kept, overlayId];
    });
  };

  /* ------------------------------------------------------------------ *
   * Preload all images.
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!car) return;
    const preload = (url?: string) => { if (isRenderableImage(url)) { const img = new Image(); img.src = url; } };
    car.overlays?.forEach((ov) => VIEW_KEYS.forEach((vk) => preload(ov.images?.[vk])));
    car.sceneries?.forEach((sc) => preload(sc.thumbnail));
    car.steps.forEach((step) => {
      step.options.forEach((option) => {
        VIEW_KEYS.forEach((vk) => {
          const url = option.images[vk];
          if (isRenderableImage(url)) { const img = new Image(); img.src = url; }
        });
        option.subOptions?.forEach((sub) => {
          VIEW_KEYS.forEach((vk) => {
            const url = sub.images[vk];
            if (isRenderableImage(url)) { const img = new Image(); img.src = url; }
          });
        });
      });
    });
  }, [car]);

  // Scenery backgrounds are big: only warm the chosen one (all views).
  useEffect(() => {
    const scenery = getActiveScenery();
    if (!scenery) return;
    VIEW_KEYS.forEach((vk) => {
      [scenery.images?.[vk], scenery.shadowImages?.[vk]].forEach((url) => {
        if (isRenderableImage(url)) { const img = new Image(); img.src = url; }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car, sceneryId]);

  /* ------------------------------------------------------------------ *
   * Screen navigation (driven by car.screens, filtered by rules).
   * ------------------------------------------------------------------ */
  const visibleScreens: Screen[] = useMemo(() => {
    if (!car) return [];
    return car.screens.filter((s) => !effects.hiddenScreens.has(s.screenId));
  }, [car, effects]);

  const visibleStepsForScreen = (screen: Screen | undefined): Step[] => {
    if (!screen || !car) return [];
    return screen.stepIds
      .map((id) => getStep(id))
      .filter((s): s is Step => !!s && !effects.hiddenSteps.has(s.stepId));
  };

  const isStepDisabled = (stepId: string) =>
    effects.disabledSteps.has(stepId) || effects.forcedOptions.has(stepId);

  /* ------------------------------------------------------------------ *
   * Image layers (data-driven; base render first).
   * ------------------------------------------------------------------ */
  const getImageLayers = (viewKey: ViewKey = selectedView) => {
    if (!car) return [];
    const layers: { url: string; type: string; zIndex: number }[] = [];

    const baseStepId = car.baseRenderOptionId ? findStepIdOfOption(car, car.baseRenderOptionId) : null;

    const pushLayer = (stepId: string, zIndex: number) => {
      const option = getSelectedOption(stepId);
      if (!option) return;
      const sub = getActiveSubOption(stepId);
      // Prefer sub image; fall back to any sub with an image for this view; then option.
      let source: { images: typeof option.images } = option;
      if (sub) {
        if (isRenderableImage(sub.images[viewKey])) source = sub;
        else {
          const fallbackSub = option.subOptions?.find((s) => isRenderableImage(s.images[viewKey]));
          source = fallbackSub || option;
        }
      }
      const url = source.images[viewKey];
      if (isRenderableImage(url)) layers.push({ url, type: `${stepId}-${option.optionId}`, zIndex });
    };

    // Active overlays with an image for this view; they may hide step layers.
    const overlaysHere = getActiveOverlays().filter((ov) => isRenderableImage(ov.images?.[viewKey]));
    const overlayHiddenSteps = new Set(overlaysHere.flatMap((ov) => ov.hideStepIds || []));

    if (baseStepId && !overlayHiddenSteps.has(baseStepId)) pushLayer(baseStepId, 0);

    car.steps.forEach((step, index) => {
      if (step.stepId === baseStepId) return;
      if (effects.hiddenSteps.has(step.stepId) || overlayHiddenSteps.has(step.stepId)) return;
      pushLayer(step.stepId, index + 1);
    });

    overlaysHere.forEach((ov) => {
      layers.push({ url: ov.images[viewKey]!, type: `overlay-${ov.overlayId}`, zIndex: overlayLayerOrder(car, ov, baseStepId) });
    });

    const scenery = getActiveScenery();
    if (scenery) {
      const bg = scenery.images?.[viewKey];
      const shadow = scenery.shadowImages?.[viewKey];
      if (isRenderableImage(bg)) layers.push({ url: bg, type: `scenery-${scenery.sceneryId}`, zIndex: SCENERY_LAYER_ORDER });
      if (isRenderableImage(shadow)) layers.push({ url: shadow, type: `scenery-shadow-${scenery.sceneryId}`, zIndex: SCENERY_SHADOW_LAYER_ORDER });
    }

    // Sort by the fractional layer order, then hand out integer ranks
    // (CSS z-index must be an integer).
    layers.sort((a, b) => a.zIndex - b.zIndex);
    return layers.map((l, i) => ({ ...l, zIndex: i + 1 }));
  };

  const allViewLayers = useMemo(() => {
    const map = {} as Record<ViewKey, { url: string; type: string; zIndex: number }[]>;
    VIEW_KEYS.forEach((vk) => { map[vk] = getImageLayers(vk); });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car, selectedOptions, selectedSubOptions, variantModes, effects, activeOverlayIds, sceneryId]);

  /* ------------------------------------------------------------------ *
   * Price.
   * ------------------------------------------------------------------ */
  const calculateTotalPrice = () => {
    if (!car) return 0;
    let total = car.basePrice || 0;
    car.steps.forEach((step) => {
      if (effects.hiddenSteps.has(step.stepId)) return;
      const option = getSelectedOption(step.stepId);
      if (!option) return;
      const isBaseRender = option.includedInBaseRender || option.optionId === car.baseRenderOptionId;
      if (!isBaseRender) total += option.optionPrice || 0;

      // Coupled (disposition-only) sub-options never add price.
      if (!option.subOptionsLinkedToStepId) {
        const sub = getActiveSubOption(step.stepId);
        if (sub) total += sub.optionPrice || 0;
      }
    });
    return total;
  };

  /* ------------------------------------------------------------------ *
   * Navigation.
   * ------------------------------------------------------------------ */
  const goToNextScreen = () => {
    setOptionsFading(true);
    if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
    setTimeout(() => {
      if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
      if (currentScreenIndex < visibleScreens.length - 1) {
        setCurrentScreenIndex((i) => i + 1);
      } else {
        setShowSummary(true);
      }
      setOptionsFading(false);
    }, 150);
  };

  const goToPreviousScreen = () => {
    if (showContact) {
      setShowContact(false);
      setShowSummary(true);
    } else if (showSummary) {
      setShowSummary(false);
      setCurrentScreenIndex(Math.max(0, visibleScreens.length - 1));
    } else if (currentScreenIndex > 0) {
      setOptionsFading(true);
      if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
      setTimeout(() => {
        if (optionsScrollRef.current) optionsScrollRef.current.scrollTop = 0;
        setCurrentScreenIndex((i) => Math.max(0, i - 1));
        setOptionsFading(false);
      }, 150);
    }
  };

  /* ------------------------------------------------------------------ *
   * Contact form.
   * ------------------------------------------------------------------ */
  const validateForm = () => {
    const errors = { firstName: '', lastName: '', email: '', phone: '' };
    let isValid = true;
    if (!formData.firstName.trim()) { errors.firstName = 'First name is required'; isValid = false; }
    if (!formData.lastName.trim()) { errors.lastName = 'Last name is required'; isValid = false; }
    if (!formData.email.trim()) { errors.email = 'Email is required'; isValid = false; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { errors.email = 'Please enter a valid email address'; isValid = false; }
    if (!formData.phone.trim()) { errors.phone = 'Phone number is required'; isValid = false; }
    setFormErrors(errors);
    return isValid;
  };

  // Flat selections array for the save-&-share API (doc §7).
  const buildSelections = () =>
    (car?.steps || [])
      .filter((step) => !effects.hiddenSteps.has(step.stepId))
      .map((step) => {
        const option = getSelectedOption(step.stepId);
        if (!option) return null;
        const sub = getActiveSubOption(step.stepId);
        return { stepId: step.stepId, optionId: option.optionId, ...(sub ? { subOptionId: sub.optionId } : {}) };
      })
      .filter((s): s is { stepId: string; optionId: string; subOptionId?: string } => !!s);

  // Persist current build and return a share URL on the page that embeds the
  // configurator (e.g. /configurator?c=:slug). The host site has no /c/ route.
  const createShareUrl = async (extra: { customerName?: string; customerEmail?: string; notes?: string } = {}) => {
    const configuration = await createConfiguration({
      carId: car?.id,
      carCreatedAt: car?.createdAt,
      selections: buildSelections(),
      activeOverlayIds,
      ...(sceneryId ? { sceneryId } : {}),
      ...extra,
    });
    return `${window.location.origin}${window.location.pathname}?c=${configuration.id}`;
  };

  const buildSelectionsText = () =>
    (car?.steps || [])
      .filter((step) => !effects.hiddenSteps.has(step.stepId))
      .map((step) => {
        const option = getSelectedOption(step.stepId);
        if (!option) return null;
        const sub = getActiveSubOption(step.stepId);
        const choice = sub ? `${option.optionName} - ${sub.optionName}` : option.optionName;
        return `${(step.stepName || step.stepId).trim()}: ${choice}`;
      })
      .filter(Boolean)
      .join('\n');

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const selectionsText = buildSelectionsText();
    const totalPriceNow = calculateTotalPrice();

    let shareableLink = '';
    try {
      shareableLink = await createShareUrl({
        customerName: `${formData.firstName} ${formData.lastName}`.trim(),
        customerEmail: (formData.email || '').trim(),
      });
    } catch (err) {
      console.error('Error creating share configuration:', err);
    }

    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(totalPriceNow) || 0);

    const leadsBodyText = [
      `Name: ${formData.firstName} ${formData.lastName}`.trim(),
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      `Car: ${car?.carName || ''}`.trim(),
      `Total Price: ${formattedPrice}`,
      `Configuration Link: ${shareableLink}`,
      ...((formData.message || '').trim() ? ['', 'Message:', formData.message.trim()] : []),
      '',
      'Selections:',
      selectionsText,
    ].filter((l) => l !== undefined && l !== null).join('\n');

    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const leadsHtml = `<pre style="font-family:Arial,sans-serif;font-size:14px;white-space:pre-wrap;">${escapeHtml(leadsBodyText)}</pre>`;

    const leadsJsonBody = JSON.stringify({
      type: 'leads',
      to: 'leads@touringdesignco.com',
      from: 'Touring Configurator <noreply@touringdesignco.com>',
      replyTo: (formData.email || '').trim(),
      // Plain fields for the Zap (sheet columns, CRM).
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: (formData.email || '').trim(),
      phone: (formData.phone || '').trim(),
      message: (formData.message || '').trim(),
      subject: `New build submission - ${formData.firstName} ${formData.lastName}`.trim(),
      text: leadsBodyText,
      html: leadsHtml,
    });

    const customerName = `${formData.firstName} ${formData.lastName}`.trim();
    const customerBodyText = [
      `Hi ${formData.firstName || 'there'},`, '',
      'Your spec is below. We’ve held it in our system, so when you’re ready to move forward - or want to make a change - it’ll be exactly where you left it.', '',
      'Your build', selectionsText, '',
      'Quoted at', formattedPrice, '',
      'When you’re ready to discuss this build - refining the spec, walking through the timeline, or talking through where you’ll take it first - our team is here.', '',
      'Reach out anytime: ryan@touringdesignco.com', '',
      '— Ryan', 'Touring',
    ].join('\n');

    const selectionsHtml = selectionsText.split('\n').map((line) => `<li>${escapeHtml(line)}</li>`).join('');
    const customerHtml = `
<div style="font-family:Arial,sans-serif;font-size:14px;color:#111;line-height:1.5;max-width:600px;">
  <img src="https://touring-configurator-prod.s3.us-east-2.amazonaws.com/email+newsletter.jpg" alt="Touring" style="display:block;width:100%;max-width:600px;height:auto;margin:0 0 16px 0;" />
  <p>Hi ${escapeHtml(formData.firstName || 'there')},</p>
  <p>Your spec is below. We&rsquo;ve held it in our system, so when you&rsquo;re ready to move forward - or want to make a change - it&rsquo;ll be exactly where you left it.</p>
  <h3 style="margin-bottom:4px;">Your build</h3>
  <ul style="padding-left:18px;margin-top:0;">${selectionsHtml}</ul>
  <p><strong>Quoted at</strong><br/>${escapeHtml(formattedPrice)}</p>
  <p>When you&rsquo;re ready to discuss this build - refining the spec, walking through the timeline, or talking through where you&rsquo;ll take it first - our team is here.</p>
  <p>Reach out anytime: <a href="mailto:ryan@touringdesignco.com">ryan@touringdesignco.com</a></p>
  <p>&mdash; Ryan<br/>Touring</p>
</div>`.trim();

    const customerJsonBody = JSON.stringify({
      type: 'customer',
      to: (formData.email || '').trim(),
      from: 'Ryan at Touring <ryan@touringdesignco.com>',
      replyTo: 'ryan@touringdesignco.com',
      subject: 'Your build, saved.',
      name: customerName,
      email: (formData.email || '').trim(),
      phone: (formData.phone || '').trim(),
      message: (formData.message || '').trim(),
      configurationLink: shareableLink,
      totalPrice: String(totalPriceNow),
      selectionsText,
      text: customerBodyText,
      html: customerHtml,
    });

    const zapierWebhookUrl = 'https://hooks.zapier.com/hooks/catch/12922324/upcaksb/';
    const sendZapier = async (jsonBody: string, label: string) => {
      if (IS_PREVIEW) {
        console.info(`[preview] Zapier ${label} webhook not sent`, JSON.parse(jsonBody));
        return;
      }
      try {
        let sent = false;
        try {
          const blob = new Blob([jsonBody], { type: 'text/plain' });
          sent = navigator.sendBeacon?.(zapierWebhookUrl, blob) ?? false;
        } catch { sent = false; }
        if (!sent) {
          await fetch(zapierWebhookUrl, { method: 'POST', body: jsonBody, keepalive: true, mode: 'no-cors' });
        }
      } catch (err) {
        console.error(`Error sending Zapier webhook (${label}):`, err);
      }
    };

    await Promise.all([sendZapier(leadsJsonBody, 'leads'), sendZapier(customerJsonBody, 'customer')]);

    // Hand the thank-you page its payload (it reads sessionStorage).
    try {
      sessionStorage.setItem('thankYouPayload', JSON.stringify({
        shareableLink,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      }));
    } catch {}

    if (IS_PREVIEW) {
      window.alert(`Preview: no lead or email was sent. Share link: ${shareableLink || '(not created)'}`);
      return;
    }
    window.location.assign('/thank-you');
  };

  const handleSaveSummary = async () => {
    try {
      const shareUrl = await createShareUrl();
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error('Error creating/copying share link:', error);
    }
  };

  /* ------------------------------------------------------------------ *
   * Render.
   * ------------------------------------------------------------------ */
  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-white ${embedded ? 'h-64' : 'min-h-screen'}`}>
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading car configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-white ${embedded ? 'h-64' : 'min-h-screen'}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!car) return null;

  const totalScreens = visibleScreens.length || 1;
  const safeScreenIndex = Math.min(currentScreenIndex, totalScreens - 1);
  const currentScreen = visibleScreens[safeScreenIndex];
  const screenSteps = visibleStepsForScreen(currentScreen);

  const isScreenComplete = screenSteps.every((step) => {
    if (isStepDisabled(step.stepId)) return true;
    const opt = getSelectedOption(step.stepId);
    if (!opt) return false;
    // Non-coupled options with variants need a sub-option chosen.
    if (!opt.subOptionsLinkedToStepId && opt.subOptions && opt.subOptions.length > 0) {
      if (!selectedSubOptions[step.stepId]) return false;
    }
    return true;
  });

  const currentImageSource = (() => {
    const first = screenSteps[0];
    if (!first) return null;
    return getActiveSubOption(first.stepId) || getSelectedOption(first.stepId);
  })();

  const getThumbnailForView = (viewKey: ViewKey) => getImageLayers(viewKey);
  const totalPrice = calculateTotalPrice();
  const isLastScreen = safeScreenIndex === totalScreens - 1;

  return (
    <div className={`${embedded ? 'h-full min-h-[700px]' : 'h-screen'} flex flex-col bg-white`}>
      <div className={`grid ${embedded ? 'grid-rows-[300px_1fr]' : 'grid-rows-[minmax(280px,42vh)_1fr] lg:flex lg:flex-row'} flex-1 gap-0 overflow-hidden min-h-0`}>
        <LayeredPreview
          carName={car.carName}
          selectedView={selectedView}
          viewLoading={viewLoading}
          allViewLayers={allViewLayers}
          imagesLoaded={imagesLoaded}
          setImagesLoaded={setImagesLoaded}
          viewKeys={VIEW_KEYS}
          setSelectedView={setSelectedView}
          setViewLoading={setViewLoading}
          stepLabel={`Step ${safeScreenIndex + 1} of ${totalScreens}`}
          stepTitle={currentScreen?.screenName || ''}
          embedded={embedded}
          overlays={car.overlays || []}
          activeOverlayIds={activeOverlayIds}
          onToggleOverlay={toggleOverlay}
          sceneries={car.sceneries || []}
          sceneryId={sceneryId}
          onSelectScenery={setSceneryId}
          onChangeModel={onChangeModel}
        />

        <div className={`w-full ${embedded ? '' : 'lg:w-96 lg:min-w-[380px]'} flex flex-col bg-white overflow-hidden order-2 lg:order-2 min-h-0`}>
          {!showSummary ? (
            <>
              <div className={`p-3 pb-0 md:p-6 md:pb-0 ${embedded ? 'hidden' : 'hidden lg:block'}`}>
                <div className="mb-2 md:mb-4">
                  <div className="text-sm text-gray-500 mb-1 md:mb-2">
                    Step {safeScreenIndex + 1} of {totalScreens}
                  </div>
                  <h2 className="text-sm font-semibold text-gray-800 mb-0.5 md:mb-1 uppercase">
                    {currentScreen?.screenName}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {currentScreen?.screenDescription
                      || screenSteps.map((s) => s.stepName).filter(Boolean).join(' • ')}
                  </p>
                </div>
              </div>

              <div ref={optionsScrollRef} className={`flex-1 min-h-0 overflow-y-auto px-4 md:px-6 pt-3 lg:pt-0 transition-opacity duration-300 ${optionsFading ? 'opacity-0' : 'opacity-100'}`}>
                {screenSteps.map((step) => {
                  const selectedOption = getSelectedOption(step.stepId);
                  const linkedStep = selectedOption?.subOptionsLinkedToStepId
                    ? getStep(selectedOption.subOptionsLinkedToStepId)
                    : undefined;
                  const hiddenOptionIds = new Set(
                    step.options
                      .filter((o) => effects.hiddenOptions.has(optionKey(step.stepId, o.optionId)))
                      .map((o) => o.optionId)
                  );
                  const disabledOptionIds = new Set(
                    step.options
                      .filter((o) => effects.disabledOptions.has(optionKey(step.stepId, o.optionId)))
                      .map((o) => o.optionId)
                  );
                  return (
                    <StepOptions
                      key={step.stepId}
                      step={step}
                      selectedOptionId={selectedOptions[step.stepId]}
                      selectedSubOptionId={selectedSubOptions[step.stepId]}
                      variantMode={variantModes[step.stepId] || 'matching'}
                      linkedStepName={linkedStep?.stepName}
                      hiddenOptionIds={hiddenOptionIds}
                      disabledOptionIds={disabledOptionIds}
                      disabled={isStepDisabled(step.stepId)}
                      onSelectOption={(optionId) => handleOptionChange(step.stepId, optionId)}
                      onSelectSubOption={(subOptionId) => handleSubOptionChange(step.stepId, subOptionId)}
                      onVariantMode={(mode, fixedSubId) => handleVariantMode(step.stepId, mode, fixedSubId)}
                      headerText={(step.stepName || 'SELECT OPTION').toUpperCase()}
                      forcedSubOptionId={getForcedSubOptionId(step.stepId)}
                    />
                  );
                })}
              </div>
            </>
          ) : showContact ? (
            <ContactForm
              formData={formData}
              formErrors={formErrors}
              setFormData={setFormData}
              handleFormSubmit={handleFormSubmit}
            />
          ) : (
            <SummaryPanel
              car={car}
              hiddenStepIds={effects.hiddenSteps}
              getSelectedOption={getSelectedOption}
              getActiveSubOption={getActiveSubOption}
            />
          )}

          <ConfiguratorFooter
            showSummary={showSummary}
            showContact={showContact}
            currentStepIndex={safeScreenIndex}
            totalSteps={totalScreens}
            isLastUiStep={isLastScreen}
            isNextDisabled={!isScreenComplete}
            viewLoading={viewLoading}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            setViewLoading={setViewLoading}
            currentImageSource={currentImageSource}
            getThumbnailForView={getThumbnailForView}
            copySuccess={copySuccess}
            handleSaveSummary={handleSaveSummary}
            goToPreviousStep={goToPreviousScreen}
            goToNextStep={goToNextScreen}
            onContact={() => setShowContact(true)}
            totalPrice={totalPrice}
            embedded={embedded}
          />
        </div>
      </div>
    </div>
  );
};

export default CarWizardConfiguratorNew;
